/**
 * AnalyticsTab — scan + submission intelligence for the target user's QR
 * Studio, fed by the getQrFormAnalytics callable (the only read path to the
 * locked-down crm_qr_scans / crm_form_submissions tables).
 *
 * Shows: 30-day scan/submission timeseries, device breakdown, per-QR rollups
 * (including the "scanned but never submitted" gap that feeds future nurture
 * outreach), and a per-form submissions browser with answers + contact links.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  HStack,
  IconButton,
  Link,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { call } from './qrStudioUtils';

const STATUS_COLORS = { synced: 'green', duplicate: 'yellow', failed: 'red', pending: 'gray' };

function shortDate(isoDay) {
  const [, month, day] = String(isoDay).split('-');
  return `${parseInt(month, 10)}/${parseInt(day, 10)}`;
}

/** First couple of answer values — a human-scannable row summary. */
function summarizeAnswers(data) {
  const values = Object.values(data || {})
    .map((a) => a?.value)
    .filter((v) => typeof v === 'string' && v.trim())
    .slice(0, 2);
  return values.join(' · ') || '—';
}

function SubmissionRow({ row, qrsById, contactsById, cardBorder }) {
  const [expanded, setExpanded] = useState(false);
  const contact = row.contact_id ? contactsById[row.contact_id] : null;
  const qr = row.qr_id ? qrsById[row.qr_id] : null;

  return (
    <>
      <Tr
        onClick={() => setExpanded((v) => !v)}
        cursor="pointer"
        _hover={{ bg: 'blackAlpha.50' }}
      >
        <Td whiteSpace="nowrap" fontSize="sm">{new Date(row.submitted_at).toLocaleString()}</Td>
        <Td fontSize="sm" maxW="260px" isTruncated>{summarizeAnswers(row.data)}</Td>
        <Td>
          <Badge colorScheme={STATUS_COLORS[row.ingest_status] || 'gray'}>{row.ingest_status}</Badge>
        </Td>
        <Td fontSize="sm">{qr ? qr.label : row.qr_id ? 'Deleted QR' : 'Direct link'}</Td>
        <Td onClick={(e) => e.stopPropagation()}>
          {contact ? (
            <Link href={`/crm/contacts/${row.contact_id}`} isExternal color="blue.500" fontSize="sm">
              {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || 'View'}{' '}
              <FiExternalLink style={{ display: 'inline' }} size={12} />
            </Link>
          ) : (
            <Text fontSize="sm" color="gray.400">—</Text>
          )}
        </Td>
        <Td w="32px">
          <IconButton
            icon={expanded ? <FiChevronUp /> : <FiChevronDown />}
            size="xs"
            variant="ghost"
            aria-label="Expand"
          />
        </Td>
      </Tr>
      {expanded && (
        <Tr>
          <Td colSpan={6} bg="blackAlpha.50">
            <Collapse in={expanded} animateOpacity>
              <VStack align="start" spacing={1.5} py={2}>
                {Object.values(row.data || {}).map((answer, idx) => (
                  <HStack key={idx} align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600" minW="160px">
                      {answer.label}
                    </Text>
                    <Text fontSize="sm">{String(answer.value)}</Text>
                  </HStack>
                ))}
                {row.ingest_error && (
                  <Text fontSize="sm" color="red.500">CRM sync error: {row.ingest_error}</Text>
                )}
              </VStack>
            </Collapse>
          </Td>
        </Tr>
      )}
    </>
  );
}

export default function AnalyticsTab({ targetUserId, qrs, forms, overview, overviewLoading, onRefreshOverview }) {
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  // Submissions browser state
  const [selectedFormId, setSelectedFormId] = useState('');
  const [submissions, setSubmissions] = useState(null);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const loadSubmissions = useCallback(async (formId, offset = 0, append = false) => {
    if (!formId) return;
    setSubmissionsLoading(true);
    try {
      const result = await call('getQrFormAnalytics', {
        userId: targetUserId,
        view: 'form_submissions',
        formId,
        limit: 50,
        offset,
      });
      setSubmissions((prev) => (
        append && prev
          ? { ...result, rows: [...prev.rows, ...result.rows], qrsById: { ...prev.qrsById, ...result.qrsById }, contactsById: { ...prev.contactsById, ...result.contactsById } }
          : result
      ));
    } catch (err) {
      toast({ title: 'Could not load submissions', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSubmissionsLoading(false);
    }
  }, [targetUserId, toast]);

  useEffect(() => {
    setSubmissions(null);
    if (selectedFormId) loadSubmissions(selectedFormId, 0, false);
  }, [selectedFormId, loadSubmissions]);

  // Default the browser to the first form once forms load
  useEffect(() => {
    if (!selectedFormId && forms.length > 0) setSelectedFormId(forms[0].id);
  }, [forms, selectedFormId]);

  const chartData = useMemo(() => {
    if (!overview) return [];
    const submissionsByDate = {};
    for (const point of overview.submissionsByDay || []) {
      submissionsByDate[point.date] = point.count;
    }
    return (overview.scansByDay || []).map((point) => ({
      date: shortDate(point.date),
      Scans: point.count,
      Submissions: submissionsByDate[point.date] || 0,
    }));
  }, [overview]);

  const deviceTotal = overview
    ? Object.values(overview.deviceBreakdown || {}).reduce((a, b) => a + b, 0)
    : 0;

  if (overviewLoading && !overview) {
    return (
      <Center py={20}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  if (!overview) {
    return (
      <Center py={20} flexDir="column">
        <Text color="gray.500" mb={3}>Analytics haven't loaded yet.</Text>
        <Button size="sm" leftIcon={<FiRefreshCw />} onClick={onRefreshOverview}>Retry</Button>
      </Center>
    );
  }

  const { totals } = overview;
  const conversion30 = totals.scans30d > 0
    ? Math.round((totals.submissions30d / totals.scans30d) * 100)
    : null;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Text color="gray.500" fontSize="sm">Last 30 days unless noted. Scans count every redirect — even visitors who never submit.</Text>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={onRefreshOverview} isLoading={overviewLoading}>
          Refresh
        </Button>
      </HStack>

      {/* KPI cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
          <StatLabel>Scans (30d)</StatLabel>
          <StatNumber>{totals.scans30d}</StatNumber>
          <StatHelpText>{totals.scansAllTime} all time</StatHelpText>
        </Stat>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
          <StatLabel>Submissions (30d)</StatLabel>
          <StatNumber>{totals.submissions30d}</StatNumber>
          <StatHelpText>{totals.submissionsAllTime} all time</StatHelpText>
        </Stat>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
          <StatLabel>Scan → Submit (30d)</StatLabel>
          <StatNumber>{conversion30 === null ? '—' : `${conversion30}%`}</StatNumber>
          <StatHelpText>conversion rate</StatHelpText>
        </Stat>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
          <StatLabel>Active</StatLabel>
          <StatNumber>{totals.activeQrCodes}</StatNumber>
          <StatHelpText>QR codes · {totals.activeForms} forms</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Timeseries */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
        <Text fontWeight="semibold" mb={3}>Scans & submissions by day</Text>
        <Box h="260px">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -24 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" fontSize={11} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} fontSize={11} />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="Scans" fill="#3182CE" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Submissions" fill="#38A169" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Device breakdown + per-QR performance */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
          <Text fontWeight="semibold" mb={3}>Scan devices (30d)</Text>
          {deviceTotal === 0 ? (
            <Text color="gray.400" fontSize="sm">No scans yet.</Text>
          ) : (
            <VStack spacing={3} align="stretch">
              {['mobile', 'tablet', 'desktop', 'other'].map((device) => {
                const count = overview.deviceBreakdown[device] || 0;
                const pct = deviceTotal ? Math.round((count / deviceTotal) * 100) : 0;
                return (
                  <Box key={device}>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" textTransform="capitalize">{device}</Text>
                      <Text fontSize="sm" color="gray.500">{count} · {pct}%</Text>
                    </HStack>
                    <Progress value={pct} size="sm" borderRadius="full" colorScheme="blue" />
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>

        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4} overflowX="auto">
          <Text fontWeight="semibold" mb={3}>QR code performance</Text>
          {qrs.length === 0 ? (
            <Text color="gray.400" fontSize="sm">No QR codes yet.</Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Code</Th>
                  <Th isNumeric>30d</Th>
                  <Th isNumeric>All time</Th>
                  <Th isNumeric>Submits</Th>
                  <Th>Last scan</Th>
                </Tr>
              </Thead>
              <Tbody>
                {qrs.map((qr) => {
                  const stats = overview.perQr?.[qr.id] || { scans30d: 0, submissions: 0 };
                  return (
                    <Tr key={qr.id}>
                      <Td maxW="160px" isTruncated>
                        <Tooltip label={`fhl.one/q/${qr.short_code}`}>{qr.label}</Tooltip>
                      </Td>
                      <Td isNumeric>{stats.scans30d}</Td>
                      <Td isNumeric>{qr.scan_count || 0}</Td>
                      <Td isNumeric>{stats.submissions}</Td>
                      <Td fontSize="xs" color="gray.500" whiteSpace="nowrap">
                        {qr.last_scanned_at ? new Date(qr.last_scanned_at).toLocaleDateString() : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </Box>
      </SimpleGrid>

      {/* Submissions browser */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="xl" p={4}>
        <HStack justify="space-between" mb={3} flexWrap="wrap" spacing={3}>
          <Text fontWeight="semibold">Submissions</Text>
          <Select
            maxW="320px"
            size="sm"
            value={selectedFormId}
            onChange={(e) => setSelectedFormId(e.target.value)}
            placeholder={forms.length ? undefined : 'No forms yet'}
          >
            {forms.map((form) => (
              <option key={form.id} value={form.id}>{form.title}</option>
            ))}
          </Select>
        </HStack>

        {submissionsLoading && !submissions ? (
          <Center py={10}><Spinner color="blue.500" /></Center>
        ) : !submissions || submissions.rows.length === 0 ? (
          <Text color="gray.400" fontSize="sm" py={6} textAlign="center">
            No submissions yet for this form.
          </Text>
        ) : (
          <>
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>When</Th>
                    <Th>Submission</Th>
                    <Th>CRM</Th>
                    <Th>Via</Th>
                    <Th>Contact</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {submissions.rows.map((row) => (
                    <SubmissionRow
                      key={row.id}
                      row={row}
                      qrsById={submissions.qrsById}
                      contactsById={submissions.contactsById}
                      cardBorder={cardBorder}
                    />
                  ))}
                </Tbody>
              </Table>
            </Box>
            <HStack justify="space-between" mt={3}>
              <Text fontSize="xs" color="gray.400">
                Showing {submissions.rows.length} of {submissions.total}
              </Text>
              {submissions.rows.length < submissions.total && (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => loadSubmissions(selectedFormId, submissions.rows.length, true)}
                  isLoading={submissionsLoading}
                >
                  Load more
                </Button>
              )}
            </HStack>
          </>
        )}
      </Box>
    </VStack>
  );
}
